import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  CalendarCheck,
  FileSpreadsheet,
  FileCheck2,
  TriangleAlert,
  Upload,
  Search,
  Filter,
} from "lucide-react";
import { usePageHeader } from "contexts/PageHeaderContext";
import { Button } from "ui/button";
import { Card, CardContent } from "ui/card";
import { Input } from "ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";

type AttendanceRecord = {
  id: number;
  sourceRow?: number;
  date: string;
  property: string;
  name: string;
  employeeId: string;
  department: string;
  status: string;
  hours: number | null;
  ot: number;
  bonus: number;
  deduction: number;
  error?: string;
};
const attendanceRecords: AttendanceRecord[] = [
  {
    id: 1,
    date: "2026-09-18",
    property: "Najaf",
    name: "Ahmed Ali",
    employeeId: "10000001",
    department: "Transport",
    status: "Present",
    hours: 10,
    ot: 2,
    bonus: 0,
    deduction: 0,
  },
  {
    id: 2,
    date: "2026-09-18",
    property: "Najaf",
    name: "Ali Hussain",
    employeeId: "10000002",
    department: "House Keeping",
    status: "Official Duty",
    hours: 9,
    ot: 1,
    bonus: 0,
    deduction: 0,
  },
  {
    id: 3,
    date: "2026-09-18",
    property: "Karbala",
    name: "Alice Johnson",
    employeeId: "10000003",
    department: "Maintenance",
    status: "Farman",
    hours: 9,
    ot: 1,
    bonus: 0,
    deduction: 0,
  },
  {
    id: 4,
    date: "2026-09-19",
    property: "Karbala",
    name: "Fatima Hassan",
    employeeId: "10000004",
    department: "House Keeping",
    status: "Present",
    hours: 8,
    ot: 0,
    bonus: 10,
    deduction: 0,
  },
  {
    id: 5,
    date: "2026-08-18",
    property: "Najaf",
    name: "Ahmed Ali",
    employeeId: "10000001",
    department: "Transport",
    status: "Present",
    hours: 8,
    ot: 0,
    bonus: 0,
    deduction: 0,
  },
  {
    id: 6,
    date: "2026-09-18",
    property: "Najaf",
    name: "Omar Khan",
    employeeId: "",
    department: "Transport",
    status: "Present",
    hours: 8,
    ot: 0,
    bonus: 0,
    deduction: 0,
    sourceRow: 142,
    error: "Missing employee ID in the imported row.",
  },
  {
    id: 7,
    date: "2026-09-18",
    property: "Karbala",
    name: "Sara Abbas",
    employeeId: "10000006",
    department: "Maintenance",
    status: "Present",
    hours: null,
    ot: 0,
    bonus: 0,
    deduction: 0,
    sourceRow: 219,
    error: "Invalid hours: a numeric value is required.",
  },
  {
    id: 8,
    date: "2026-09-18",
    property: "Najaf",
    name: "Ahmed Ali",
    employeeId: "10000001",
    department: "Transport",
    status: "Present",
    hours: 10,
    ot: 2,
    bonus: 0,
    deduction: 0,
    sourceRow: 312,
    error: "Duplicate attendance for this employee and date.",
  },
];
const properties = Array.from(
  new Set(attendanceRecords.map((row) => row.property)),
);
const departments = Array.from(
  new Set(attendanceRecords.map((row) => row.department)),
);
const statuses = Array.from(
  new Set(attendanceRecords.map((row) => row.status)),
);
const columns = [
  "Date",
  "Employee Name",
  "Emp ID",
  "Department",
  "Status",
  "Hours",
  "OT",
  "Bonus",
  "Deduction",
];

export function AttendancePage() {
  const { setPageHeader } = usePageHeader();
  const [property, setProperty] = useState("");
  const [month, setMonth] = useState("");
  const [filename, setFilename] = useState("");
  const [fileError, setFileError] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("clean");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [department, setDepartment] = useState("all");
  const [status, setStatus] = useState("all");
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPageHeader({ title: "Attendance", icon: <CalendarCheck /> });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const query = search.trim().toLowerCase();
  const filtered = attendanceRecords.filter(
    (row) =>
      (!property || property === "all" || row.property === property) &&
      (!month || row.date.startsWith(month)) &&
      `${row.name} ${row.employeeId}`.toLowerCase().includes(query) &&
      (department === "all" || row.department === department) &&
      (status === "all" || row.status === status),
  );
  const clean = filtered.filter((row) => !row.error);
  const failed = filtered.filter((row) => !!row.error);
  const kpis = [
    {
      label: "Total Records",
      value: filtered.length,
      icon: FileSpreadsheet,
      type: "total",
      cardClassName: "!border-[#bfdbfe] !bg-[#eff6ff]",
      accentClassName: "text-[#3973ff]",
      valueClassName: "text-[#304c99]",
      iconClassName: "bg-[#dbeafe]",
    },
    {
      label: "Clean Records",
      value: clean.length,
      icon: FileCheck2,
      type: "clean",
      cardClassName: "!border-[#bbf7d0] !bg-[#f0faf7]",
      accentClassName: "text-[#22b85d]",
      valueClassName: "text-[#166534]",
      iconClassName: "bg-[#dcfce7]",
    },
    {
      label: "Failed Records",
      value: failed.length,
      icon: TriangleAlert,
      type: "failed",
      cardClassName: "!border-[#fed7aa] !bg-[#faf8f5]",
      accentClassName: "text-[#f97316]",
      valueClassName: "text-[#863416]",
      iconClassName: "bg-[#ffedd5]",
    },
  ];

  const chooseFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      setFileError("Choose an .xlsx, .xls, or .csv file.");
      event.target.value = "";
      return;
    }
    setFilename(file.name);
    setFileError("");
    // Selection only: this mock screen never reads, uploads, or persists the file.
    event.target.value = "";
  };

  const table = (rows: AttendanceRecord[], showErrors: boolean) => (
    <Table className="min-w-[1000px] [&_th]:bg-[#e3f2fd] [&_th]:px-6 [&_th]:py-4 [&_th]:font-semibold [&_th]:text-[#086b94] [&_td]:px-6 [&_td]:py-[18px] [&_td]:text-gray-500 [&_tr]:border-[#dce7f1]">
      <TableHeader>
        <TableRow>
          {showErrors && <TableHead>Row#</TableHead>}
          {[...columns, ...(showErrors ? ["Import Error"] : [])].map(
            (column) => (
              <TableHead key={column}>{column}</TableHead>
            ),
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            {showErrors && (
              <TableCell className="font-medium !text-gray-700">
                Row {row.sourceRow}
              </TableCell>
            )}
            <TableCell>
              {new Date(`${row.date}T12:00:00`).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
              })}
            </TableCell>
            <TableCell className="font-medium !text-gray-700">
              {row.name}
            </TableCell>
            <TableCell>{row.employeeId || "—"}</TableCell>
            <TableCell>{row.department}</TableCell>
            <TableCell>{row.status}</TableCell>
            <TableCell>{row.hours ?? "—"}</TableCell>
            <TableCell>{row.ot}</TableCell>
            <TableCell>{row.bonus}</TableCell>
            <TableCell>{row.deduction}</TableCell>
            {showErrors && (
              <TableCell className="min-w-[240px] !whitespace-normal !text-[#c2410c]">
                {row.error}
              </TableCell>
            )}
          </TableRow>
        ))}
        {!rows.length && (
          <TableRow>
            <TableCell
              colSpan={showErrors ? 11 : 9}
              className="!p-8 text-center"
            >
              No {showErrors ? "failed" : "clean"} records match the selected
              filters.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
  
  return (
    <section
      id="hr-attendance"
      aria-label="Attendance"
      className="flex flex-col gap-6 text-[#36577f]"
    >
      <Card className="!mt-0 !gap-0 !rounded-2xl !border-[#ccecff] !bg-white !shadow-none">
        <CardContent className="!px-6 !py-4">
          <h2 className="mb-2 text-[16px] font-semibold text-[#086b94]">
            Import Attendance
          </h2>
          <div className="grid grid-cols-[1fr_1fr_1.2fr_auto] items-end gap-6 max-[1100px]:grid-cols-2 max-[640px]:grid-cols-1 [&_fieldset]:min-w-0 [&_fieldset]:!border [&_fieldset]:!border-[#d1e3f5] [&_fieldset]:!px-3 [&_fieldset]:!pb-1.5 [&_fieldset]:!pt-0 [&_fieldset]:!shadow-none [&_legend]:!mb-0 [&_fieldset>div]:!mt-0 [&_input]:!h-6 [&_input]:min-h-6 [&_input]:!py-0 [&_input]:text-[14px] [&_input]:!shadow-none [&_input[type=month]]:relative [&_input[type=month]]:!pr-7 [&_input::-webkit-calendar-picker-indicator]:absolute [&_input::-webkit-calendar-picker-indicator]:right-0 [&_input::-webkit-calendar-picker-indicator]:m-0 [&_input::-webkit-calendar-picker-indicator]:h-5 [&_input::-webkit-calendar-picker-indicator]:w-5">
            <SelectFieldset label="Property" required>
              <Select value={property} onValueChange={setProperty}>
                <SelectTriggerUI aria-label="Property" aria-required="true">
                  <SelectValue placeholder="Choose Property" />
                </SelectTriggerUI>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {properties.map((value) => (
                    <SelectItem key={value} value={value}>
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SelectFieldset>
            <InputFieldset
              label="Month"
              type="month"
              aria-label="Month"
              required
              value={month}
              onChange={(event) => setMonth(event.target.value)}
            />
            <InputFieldset
              label="Attendance Excel"
              aria-label="Attendance Excel"
              placeholder="No File Chosen (Accepted formats: .xlsx, .xls, .csv)"
              readOnly
              value={filename}
              title={filename || "Accepted formats: .xlsx, .xls, .csv"}
            />
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="sr-only"
              tabIndex={-1}
              aria-label="Choose attendance file"
              onChange={chooseFile}
            />
            <Button
              variant="outline"
              onClick={() => fileInput.current?.click()}
              className="!border-[#d1e3f5] !bg-[#f0f4f8] !px-6 !py-0 !text-[#294a73]"
            >
              <Upload />
              Choose File
            </Button>
          </div>
          {fileError && (
            <p role="alert" className="mt-3 text-[14px] text-red-700">
              {fileError}
            </p>
          )}
          {filename && (
            <p role="status" className="mt-3 text-[12px] text-slate-500">
              Selected: {filename}. Preview only; the mock attendance records
              are unchanged.
            </p>
          )}
        </CardContent>
      </Card>
      <div className="grid grid-cols-3 gap-5 max-[640px]:grid-cols-1">
        {kpis.map(
          ({
            label,
            value,
            icon: Icon,
            type,
            cardClassName,
            accentClassName,
            valueClassName,
            iconClassName,
          }) => (
            <Card
              key={type}
              className={`!mt-0 !rounded-2xl border-2 !shadow-[0_5px_10px_#203e6515] ${cardClassName}`}
              aria-label={`${label} total`}
            >
              <CardContent className="flex items-center justify-between gap-4 !p-6">
                <div>
                  <p className={`text-[16px] ${accentClassName}`}>{label}</p>
                  <strong
                    className={`mt-1 block text-2xl font-semibold ${valueClassName}`}
                  >
                    {value.toLocaleString("en-US")}
                  </strong>
                </div>
                <span
                  className={`flex h-14 w-14 items-center justify-center rounded-full ${iconClassName} ${accentClassName}`}
                >
                  <Icon className="h-[26px] w-[26px]" />
                </span>
              </CardContent>
            </Card>
          ),
        )}
      </div>
      <Card className="!mt-0 !gap-0 overflow-hidden !rounded-2xl !border-[#ccecff] !bg-white !shadow-none">
        <Tabs value={tab} onValueChange={setTab} className="!gap-0">
          <div className="flex flex-wrap items-center gap-6 px-6 py-3 max-[640px]:gap-3 max-[640px]:px-4">
            <TabsList
              aria-label="Attendance records"
              className="!h-10 !rounded-[24px] !bg-[#f8fbfd] !p-0"
            >
              <TabsTrigger
                value="clean"
                className="!h-10 !rounded-[24px] !px-[18px] !py-2 !text-[#294a73] data-[state=active]:!bg-[#5b9bd5] data-[state=active]:!text-white"
              >
                Clean Records
              </TabsTrigger>
              <TabsTrigger
                value="failed"
                className="!h-10 !rounded-[24px] !px-[18px] !py-2 !text-[#294a73] data-[state=active]:!bg-[#5b9bd5] data-[state=active]:!text-white"
              >
                Failed Records
              </TabsTrigger>
            </TabsList>
            <div className="relative ml-auto w-80 max-w-full max-[640px]:ml-0 max-[640px]:min-w-[180px] max-[640px]:flex-1">
              <Search
                aria-hidden="true"
                className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#a3a3a3]"
              />
              <Input
                aria-label="Search by Employee Name / ID"
                placeholder="Search by Employee Name / ID"
                className="!h-10 !bg-[#f8fbfd] !pl-10"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              aria-label="Filter attendance records"
              aria-expanded={filtersOpen}
              aria-controls="attendance-filters"
              className="!h-10 !w-10 !border-[#5b9bd5] !text-slate-600"
              onClick={() => setFiltersOpen((open) => !open)}
            >
              <Filter />
            </Button>
          </div>
          {filtersOpen && (
            <div
              id="attendance-filters"
              className="flex flex-wrap items-end gap-4 px-6 pb-5 [&>div]:min-w-[180px]"
            >
              <SelectFieldset label="Department">
                <Select value={department} onValueChange={setDepartment}>
                  <SelectTriggerUI aria-label="Department filter">
                    <SelectValue />
                  </SelectTriggerUI>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </SelectFieldset>
              <SelectFieldset label="Status">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTriggerUI aria-label="Status filter">
                    <SelectValue />
                  </SelectTriggerUI>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {statuses.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </SelectFieldset>
              <Button
                variant="outline"
                onClick={() => {
                  setDepartment("all");
                  setStatus("all");
                  setProperty("");
                  setMonth("");
                  setSearch("");
                }}
              >
                Reset Filters
              </Button>
            </div>
          )}
          <TabsContent value="clean">{table(clean, false)}</TabsContent>
          <TabsContent value="failed">{table(failed, true)}</TabsContent>
        </Tabs>
      </Card>
    </section>
  );
}
